/**
 * Shell-level notices every layout shares: the open-editors cap, a session
 * that expired with unsaved editors, and the floating "Exit full screen".
 */
import { AlertTriangle, LogIn, Minimize2, Trash2, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../navIcons'
import { S, iconButton } from '../tokens'
import type { ShellNavModel } from '../useShellNav'
import { EditorsList, editorTitle } from './EditorsList'

export function CapNotice({ model, onOpenList }: { model: ShellNavModel; onOpenList?: () => void }) {
  if (!model.capNotice) return null
  return (
    <div role="alert" {...tw('msg', 'warning', { kind: 'shell-editor-cap' })} style={{
      position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 9446,
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 'min(560px, calc(100vw - 32px))',
      padding: '12px 14px', borderRadius: 12, background: S.raised, color: S.text,
      border: `1px solid ${S.border}`, boxShadow: '0 14px 34px rgba(0,0,0,0.24)', fontSize: 14.5,
    }}>
      <AlertTriangle size={18} color={S.warning} aria-hidden style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, lineHeight: 1.45 }}>
        You already have {model.capNotice.max} records open — the most at once. Save or close one to start another.
      </span>
      {onOpenList && (
        <button type="button" className="tw-sh-btn" onClick={() => { model.dismissCapNotice(); onOpenList() }}
          {...tw('action', 'shell-editors-open-list')}
          style={{
            height: 34, padding: '0 12px', border: `1px solid ${S.border}`, borderRadius: S.radius,
            background: 'transparent', color: S.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
            whiteSpace: 'nowrap',
          }}>
          Open editors
        </button>
      )}
      <button type="button" className="tw-sh-btn" onClick={model.dismissCapNotice} aria-label="Dismiss"
        style={iconButton(32)}>
        <X size={15} aria-hidden />
      </button>
    </div>
  )
}

/** The session ended while editors held unsaved work: list them, and ask. */
export function ExpiredEditorsDialog({ model, onSignIn }: { model: ShellNavModel; onSignIn?: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const show = model.sessionExpired && model.dirtyEditors.length > 0
  useEffect(() => { if (show) ref.current?.querySelector<HTMLElement>('button')?.focus() }, [show])
  if (!show) return null
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9458, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={ref} role="alertdialog" aria-modal="true" aria-labelledby="tw-sh-expired-h"
        data-tw-dialog="shell-session-expired"
        style={{ width: 'min(480px, 100%)', borderRadius: 14, background: S.raised, color: S.text,
          border: `1px solid ${S.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.35)', padding: 18 }}>
        <h2 id="tw-sh-expired-h" style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 600 }}>Your session has ended</h2>
        <p style={{ margin: '0 0 10px', fontSize: 14.5, color: S.text2, lineHeight: 1.5 }}>
          {model.dirtyEditors.length === 1 ? 'This record has' : 'These records have'} unsaved changes.
          Sign in again as the same person to pick them up, or discard them.
        </p>
        <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: 14.5, lineHeight: 1.6 }}>
          {model.dirtyEditors.map((s) => (
            <li key={s.id}>{editorTitle(s)} <span style={{ color: S.text2 }}>· {model.itemFor(s.objName)?.title || s.objName}</span></li>
          ))}
        </ul>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="tw-sh-btn" onClick={model.discardAllEditors}
            {...tw('action', 'shell-expired-discard')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 38, padding: '0 14px',
              border: `1px solid ${S.border}`, borderRadius: S.radius, background: 'transparent', color: S.error,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 14.5 }}>
            <Trash2 size={15} aria-hidden /> Discard
          </button>
          {onSignIn && (
            <button type="button" onClick={onSignIn} {...tw('action', 'shell-expired-signin')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 38, padding: '0 14px',
                border: 'none', borderRadius: S.radius, background: S.accent, color: '#fff',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 600 }}>
              <LogIn size={15} aria-hidden /> Sign in again
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Full screen's one piece of chrome: a slim bar ABOVE the screen — its title
 * and the visible way out (Esc does the same). In the page flow, never floating:
 * a floating control always ends up over some screen's own toolbar button.
 */
export function ExitFullScreen({ model }: { model: ShellNavModel }) {
  if (!model.fullScreen) return null
  const item = model.current
  return (
    <div role="region" aria-label="Full screen" {...tw('action', 'shell-fullscreen-bar')} style={{
      flexShrink: 0, height: 36, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 8,
      padding: '0 8px 0 12px', background: S.panel, borderBottom: `1px solid ${S.border}`, color: S.text,
    }}>
      {item && <NavIcon node={item} size={15} color={S.accent} />}
      <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>{item?.title || ''}</span>
      <button type="button" onClick={() => model.setFullScreen(false)} title="Exit full screen (Esc)"
        {...tw('action', 'shell-exit-fullscreen')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px', flexShrink: 0,
          borderRadius: 7, border: `1px solid ${S.border}`, background: 'transparent', color: S.text,
          cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5,
        }}>
        <Minimize2 size={14} aria-hidden /> Exit full screen
      </button>
    </div>
  )
}

/** A small panel listing open editors (for layouts whose idiom is a drop-down). */
export function EditorsPanel({ model, onClose, title = 'Open editors' }: {
  model: ShellNavModel; onClose: () => void; title?: string
}) {
  return (
    <div style={{ width: 340, maxWidth: 'calc(100vw - 24px)', padding: '6px 4px', borderRadius: 12,
      background: S.raised, color: S.text, border: `1px solid ${S.border}`, boxShadow: '0 14px 34px rgba(0,0,0,0.24)' }}>
      <div style={{ padding: '6px 12px 8px', borderBottom: `1px solid ${S.border}`, marginBottom: 4 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 13, color: S.text2 }}>Records you were adding or editing, kept as you left them.</div>
      </div>
      <EditorsList model={model} onPicked={onClose} />
    </div>
  )
}
